/**
 * splinkUrl — defensive helpers for rendering short URLs + QR codes.
 *
 * Rules the whole site now enforces at render time (regardless of what
 * the DB happens to hold):
 *   1. Any legacy `fp.to/<code>` gets rewritten to `splink.pro/<code>`.
 *      The prod-side backfill covers most rows, this is the safety net.
 *   2. Asset URLs (QR PNGs, barcode PNGs) that are already absolute pass
 *      through untouched. Relative paths get prefixed with `apiBase`.
 *   3. When a short URL is present but the row is missing a QR URL, we
 *      synthesize one from SpLink's public `/api/qr/{slug}` endpoint.
 *
 * These helpers are pure — safe to import from any component.
 */

/** Normalise a stored short URL. Rewrites `fp.to` → `splink.pro`. */
export function normalizeShortUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const fpto = raw.match(/^https?:\/\/fp\.to\/(.+?)\/?$/i);
  if (fpto) return `https://splink.pro/${fpto[1]}`;
  return raw;
}

/**
 * Normalise a stored asset URL (QR PNG, barcode PNG, …).
 *   • Absolute (https://…)  → returned as-is.
 *   • Relative (/api/…)     → prefixed with apiBase.
 *   • Falsy                 → null so callers can `if (url) render…`.
 */
export function normalizeAssetUrl(raw, apiBase) {
  if (!raw || typeof raw !== "string") return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${apiBase || ""}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

/**
 * When we have a short URL but no QR image URL, derive the public QR
 * endpoint from the domain + slug (SpLink v1 exposes /api/qr/{slug}).
 */
export function synthesizeQrFromShortUrl(shortUrl) {
  if (!shortUrl) return null;
  const m = shortUrl.match(/^https?:\/\/([^/]+)\/([A-Za-z0-9_-]+)\/?$/);
  if (!m) return null;
  return `https://${m[1]}/api/qr/${m[2]}?fmt=png`;
}

/**
 * Human-friendly variant that strips the scheme so `splink.pro/abc` can
 * be displayed without the `https://` prefix cluttering the UI. Applies
 * the fp.to → splink.pro rewrite first.
 */
export function displayShortUrl(raw) {
  const norm = normalizeShortUrl(raw);
  if (!norm) return null;
  return norm.replace(/^https?:\/\//, "");
}
